import { registerEntityActions } from '@/actions/entityActionRegistry';
import type { EntityActionHandler } from '@/actions/entityActionRegistry';
import type { LocationActionDialogState } from '@/components/locations/LocationActionDialogs';

const LOCATION_DIALOG_KEY = 'location.actions';

const requireDialog = (
  handlerId: string,
  openDialog?: <Payload = unknown>(key: string, payload?: Payload) => void,
) => {
  if (!openDialog) {
    throw new Error(`${handlerId} action requires dialog controls.`);
  }
  return openDialog;
};

const requireEntity = <T>(handlerId: string, entity?: T): T => {
  if (!entity) {
    throw new Error(`${handlerId} action requires an entity context.`);
  }
  return entity;
};

const createHandler: EntityActionHandler = (context, action) => {
  const { queryClient, notifications } = context;
  const openDialog = requireDialog('location.create', context.openDialog);
  const defaultParentId = action?.options?.defaultParentId as string | undefined;
  openDialog<LocationActionDialogState>(LOCATION_DIALOG_KEY, {
    type: 'create',
    defaultParentId: defaultParentId ?? null,
    queryClient,
    notifications,
  });
};

const editHandler: EntityActionHandler = (context) => {
  const { entity, queryClient, notifications } = context;
  const openDialog = requireDialog('location.edit', context.openDialog);
  const target = requireEntity('location.edit', entity);
  openDialog<LocationActionDialogState>(LOCATION_DIALOG_KEY, {
    type: 'edit',
    entity: target,
    queryClient,
    notifications,
  });
};

const viewHandler: EntityActionHandler = (context) => {
  const { entity, navigate, returnSearch } = context;
  const target = requireEntity('location.view', entity);
  if (navigate) {
    navigate(`/location/${target.id}`, { state: { returnSearch } });
  }
};

const deleteHandler: EntityActionHandler = (context) => {
  const { entity, queryClient, notifications } = context;
  const openDialog = requireDialog('location.delete', context.openDialog);
  const target = requireEntity('location.delete', entity);
  openDialog<LocationActionDialogState>(LOCATION_DIALOG_KEY, {
    type: 'delete',
    entity: target,
    queryClient,
    notifications,
  });
};

registerEntityActions({
  'location.create': createHandler,
  'location.edit': editHandler,
  'location.view': viewHandler,
  'location.delete': deleteHandler,
});
